import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, DragIndicator, Timer, Build as BuildIcon, Image as ImageIcon, CloudUploadOutlined, ImageOutlined } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RecipeV2Detail, RecipeStep, CreateUpdateStepRequest, ProductionMethod } from '../types';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { prepareRecipeImage } from '../recipeMediaUpload';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { Trash01 } from '../../../assets';

interface MethodTabProps {
    recipe: RecipeV2Detail;
    editable: boolean;
    onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

interface StepCardProps {
    step: RecipeStep;
    index: number;
    editable: boolean;
    productionMethodName?: string;
    onEdit: (step: RecipeStep) => void;
    onRemove: (stepId: number) => void;
}

/**
 * Draggable step card, laid out in a responsive grid (see the `Box` grid container in
 * MethodTab below). Each card is a self-contained, vertically-stacked block of the
 * step's structured fields - title, description, method type/duration/temperature tags,
 * and equipment - styled after the Task Templates step builder. Plain display only;
 * editing happens via the Add/Edit dialog.
 */
const StepCard = ({ step, index, editable, productionMethodName, onEdit, onRemove }: StepCardProps) => {
  const { t } = useTranslation('recipeV2');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={() => editable && onEdit(step)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        cursor: editable ? 'pointer' : 'default',
        '&:hover': editable ? { borderColor: 'primary.main' } : undefined,
      }}
    >
      <Box sx={{ position: 'relative', height: 200, flexShrink: 0 }}>
        {step.mediaUrl ? <Box component="img"
          src={step.mediaUrl}
          alt=""
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (
          <Box aria-hidden="true"
            sx={{ width: '100%', height: '100%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
            <ImageIcon />
          </Box>
        )}
        <Stack direction="row"
          alignItems="center"
          spacing={1}
          sx={{ position: 'absolute', top: 12, left: 12 }}>
          {editable && (
            <Box
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary', cursor: 'grab', borderRadius: '50%', bgcolor: 'rgba(255, 255, 255, 0.82)', backdropFilter: 'blur(4px)' }}
            >
              <DragIndicator fontSize="small" />
            </Box>
          )}
          <Chip
            label={index + 1}
            size="small"
            sx={(theme) => ({ minWidth: 32, height: 28, fontWeight: 700, bgcolor: theme.palette.custom.gray[800], color: 'common.white', backdropFilter: 'blur(4px)' })}
          />
        </Stack>
        {editable && (
          <Stack direction="row"
            spacing={0.5}
            onClick={(e) => e.stopPropagation()}
            sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton size="small"
              onClick={() => onEdit(step)}
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.82)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.96)' } }}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small"
              color="error"
              onClick={() => onRemove(step.id)}
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.82)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.96)' } }}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography variant="textMD"
          fontWeight={600}>
          {step.title || `${t('build.steps')} ${index + 1}`}
        </Typography>
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ mt: 1, flex: 1 }}>
          {step.instruction}
        </Typography>

        {(productionMethodName || step.durationMinutes != null || step.temperature) && (
          <Stack direction="row"
            spacing={1}
            sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap>
            {productionMethodName && (
              <Chip icon={<BuildIcon sx={{ fontSize: 14 }} />}
                label={productionMethodName}
                size="small"
                variant="outlined" />
            )}
            {step.durationMinutes != null && (
              <Chip icon={<Timer sx={{ fontSize: 14 }} />}
                label={`${step.durationMinutes} min`}
                size="small"
                variant="outlined" />
            )}
            {step.temperature && (
              <Chip label={`🌡️ ${step.temperature}`}
                size="small"
                variant="outlined" />
            )}
          </Stack>
        )}
        {step.equipment.length > 0 && (
          <Stack
            sx={{ mt: 2 }}>
            <Stack direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap>
              {step.equipment.map((eq) => (
                <Chip key={eq.id}
                  label={eq.name}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }} />
              ))}
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

interface StepFormState {
    title: string;
    instruction: string;
    productionMethodId: number | '';
    durationMinutes: string;
    temperature: string;
    equipmentNames: string[];
    imageFile: File | null;
    imagePreviewUrl: string | null;
    removeExistingImage: boolean;
}

const emptyStepForm: StepFormState = {
  title: '',
  instruction: '',
  productionMethodId: '',
  durationMinutes: '',
  temperature: '',
  equipmentNames: [],
  imageFile: null,
  imagePreviewUrl: null,
  removeExistingImage: false,
};

const DEFAULT_EQUIPMENT_OPTIONS = ['Oven', 'Baking tray', 'Whisk', 'Mixing bowl', 'Saucepan', 'Frying pan', 'Knife', 'Chopping board', 'Measuring jug', 'Tongs', 'Spatula', 'Food thermometer'];

const deduplicateEquipment = (names: string[]) => Array.from(new Map(names.map((name) => [name.trim().toLocaleLowerCase(), name.trim()])).values()).filter(Boolean);

const MethodTab = ({ recipe, editable, onMetadataChange }: MethodTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const [steps, setSteps] = useState<RecipeStep[]>(recipe.steps ?? []);
  const [productionMethods, setProductionMethods] = useState<ProductionMethod[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<RecipeStep | null>(null);
  const [stepForm, setStepForm] = useState<StepFormState>(emptyStepForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMediaStepId, setPendingMediaStepId] = useState<number | null>(null);
  const [stepPendingDelete, setStepPendingDelete] = useState<RecipeStep | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api.getProductionMethods(recipe.franchiseId).then(setProductionMethods).catch(() => setProductionMethods([]));
  }, [api, recipe.franchiseId]);

  useEffect(() => {
    setSteps(recipe.steps ?? []);
  }, [recipe.id, recipe.steps]);

  useEffect(() => {
    let active = true;
    Promise.all((recipe.steps ?? []).map(async step => ({ id: step.id, url: await api.getRecipeMediaUrl(recipe.id, step.id) })))
      .then(images => active && setSteps(current => current.map(step => ({ ...step, mediaUrl: images.find(image => image.id === step.id)?.url ?? null }))))
      .catch(() => active && setError('Some recipe images could not be loaded.'));
    return () => { active = false; };
  }, [api, recipe.id]);

  const productionMethodName = (id: number | null) => productionMethods.find((m) => m.id === id)?.name;

  const equipmentOptions = useMemo(() => {
    return deduplicateEquipment([...DEFAULT_EQUIPMENT_OPTIONS, ...steps.flatMap((step) => step.equipment.map((equipment) => equipment.name))]);
  }, [steps]);

  const releasePreviewUrl = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
  };

  const resetStepDialog = () => {
    releasePreviewUrl();
    setStepForm(emptyStepForm);
    setEditingStep(null);
    setAddDialogOpen(false);
    setPendingMediaStepId(null);
  };

  useEffect(() => () => releasePreviewUrl(), []);

  const selectImage = (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Select a JPEG, PNG, or WebP image.');
      return;
    }
    releasePreviewUrl();
    const imagePreviewUrl = URL.createObjectURL(file);
    previewUrlRef.current = imagePreviewUrl;
    setStepForm((current) => ({ ...current, imageFile: file, imagePreviewUrl, removeExistingImage: false }));
  };

  const removeSelectedImage = () => {
    releasePreviewUrl();
    setStepForm((current) => ({ ...current, imageFile: null, imagePreviewUrl: null, removeExistingImage: true }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const previous = steps;
    const oldIndex = previous.findIndex((s) => s.id === active.id);
    const newIndex = previous.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(previous, oldIndex, newIndex).map((s, i) => ({ ...s, stepNumber: i + 1 }));
    setSteps(reordered);
    try { await api.reorderSteps(recipe.id, reordered.map((s) => s.id)); }
    catch (cause) { setSteps(previous); setError(cause instanceof Error ? cause.message : 'Failed to reorder steps.'); }
  };

  const applyMediaChange = async (stepId: number) => {
    if (stepForm.imageFile) {
      const prepared = await prepareRecipeImage(stepForm.imageFile);
      const upload = await api.authorizeRecipeMediaUpload(recipe.id, prepared, stepId);
      const response = await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': prepared.type }, body: prepared });
      if (!response.ok) throw new Error('Image upload to storage failed.');
      await api.finalizeRecipeMediaUpload(recipe.id, upload.uploadId);
      const mediaUrl = await api.getRecipeMediaUrl(recipe.id, stepId);
      setSteps((current) => current.map((step) => step.id === stepId ? { ...step, mediaUrl } : step));
      return;
    }
    if (stepForm.removeExistingImage) {
      await api.removeRecipeMedia(recipe.id, stepId);
      setSteps((current) => current.map((step) => step.id === stepId ? { ...step, mediaUrl: null } : step));
    }
  };

  const handleAddStep = async () => {
    const newStep: CreateUpdateStepRequest = {
      title: stepForm.title,
      instruction: stepForm.instruction,
      productionMethodId: stepForm.productionMethodId === '' ? null : stepForm.productionMethodId,
      durationMinutes: stepForm.durationMinutes ? Number(stepForm.durationMinutes) : null,
      temperature: stepForm.temperature || null,
      equipmentNames: stepForm.equipmentNames,
    };
    setIsSubmitting(true); setError(null);
    try {
      const saved = await api.addStep(recipe.id, newStep);
      setSteps((prev) => [...prev, saved]);
      setSuccessMessage('Step added successfully.');
      if (stepForm.imageFile) {
        try {
          await applyMediaChange(saved.id);
        } catch (cause) {
          setEditingStep(saved);
          setAddDialogOpen(false);
          setPendingMediaStepId(saved.id);
          setError(cause instanceof Error ? `Step was saved, but the image could not be uploaded. Retry the image upload. ${cause.message}` : 'Step was saved, but the image could not be uploaded. Retry the image upload.');
          return;
        }
      }
      resetStepDialog();
    } catch (cause) { setError(cause instanceof Error ? `Step failed to save. ${cause.message}` : 'Step failed to save.'); }
    finally { setIsSubmitting(false); }
  };

  const handleUpdateStep = async () => {
    if (!editingStep) return;
    if (pendingMediaStepId === editingStep.id) {
      setIsSubmitting(true); setError(null);
      try {
        await applyMediaChange(editingStep.id);
        resetStepDialog();
      } catch (cause) { setError(cause instanceof Error ? `Step details were saved, but the image action failed. Retry the image upload. ${cause.message}` : 'Step details were saved, but the image action failed. Retry the image upload.'); }
      finally { setIsSubmitting(false); }
      return;
    }
    const updated: CreateUpdateStepRequest = {
      title: stepForm.title,
      instruction: stepForm.instruction,
      productionMethodId: stepForm.productionMethodId === '' ? null : stepForm.productionMethodId,
      durationMinutes: stepForm.durationMinutes ? Number(stepForm.durationMinutes) : null,
      temperature: stepForm.temperature || null,
      equipmentNames: stepForm.equipmentNames,
    };
    setIsSubmitting(true); setError(null);
    try {
      const saved = await api.updateStep(recipe.id, editingStep.id, updated);
      const savedWithMedia = { ...saved, mediaUrl: editingStep.mediaUrl };
      setSteps((prev) => prev.map((step) => step.id === editingStep.id ? savedWithMedia : step));
      if (stepForm.imageFile || stepForm.removeExistingImage) {
        setEditingStep(savedWithMedia);
        try {
          await applyMediaChange(editingStep.id);
        } catch (cause) {
          setPendingMediaStepId(editingStep.id);
          setError(cause instanceof Error ? `Step details were saved, but the image action failed. Retry the image upload. ${cause.message}` : 'Step details were saved, but the image action failed. Retry the image upload.');
          return;
        }
      }
      resetStepDialog();
    } catch (cause) { setError(cause instanceof Error ? `Step failed to save. ${cause.message}` : 'Step failed to save.'); }
    finally { setIsSubmitting(false); }
  };

  const handleRemoveStep = async (stepId: number): Promise<boolean> => {
    setIsSubmitting(true); setError(null);
    try {
      await api.removeStep(recipe.id, stepId);
      const remainingSteps = steps
        .filter((step) => step.id !== stepId)
        .map((step, index) => ({ ...step, stepNumber: index + 1 }));
      setSteps(remainingSteps);
      onMetadataChange?.({ steps: remainingSteps });
      setSuccessMessage('Step deleted successfully.');
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to remove step.');
      return false;
    }
    finally { setIsSubmitting(false); }
  };

  const openEditStep = (step: RecipeStep) => {
    releasePreviewUrl();
    setPendingMediaStepId(null);
    setEditingStep(step);
    setStepForm({
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId ?? '',
      durationMinutes: step.durationMinutes?.toString() ?? '',
      temperature: step.temperature ?? '',
      equipmentNames: step.equipment.map((e) => e.name),
      imageFile: null,
      imagePreviewUrl: null,
      removeExistingImage: false,
    });
  };

  const handleProductionMethodChange = (value: number | '') => {
    const method = value === '' ? null : productionMethods.find((m) => m.id === value) ?? null;
    onMetadataChange?.({ productionMethodId: value === '' ? null : value, productionMethod: method });
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error"
        onClose={() => setError(null)}>{error}</Alert>}
      {/* Recipe method meta bar - fields styled the same as the create wizard (TextField + built-in label), editable */}
      <Stack spacing={2}
        sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row"
          spacing={3}
          alignItems="flex-start"
          flexWrap="wrap">
          <TextField
            label={t('build.productionMethod')}
            select
            value={recipe.productionMethodId ?? ''}
            onChange={(e) => handleProductionMethodChange(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={!editable}
            sx={{ width: 200 }}
          >
            <MenuItem value="">{t('create.none')}</MenuItem>
            {productionMethods.map((pm) => (
              <MenuItem key={pm.id}
                value={pm.id}>{pm.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('create.effectiveFrom', 'Effective From')}
            type="date"
            value={recipe.effectiveFrom?.substring(0, 10) ?? ''}
            onChange={(e) => onMetadataChange?.({ effectiveFrom: String(e.target.value || '') })}
            disabled={!editable}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <TextField
            label={t('create.effectiveTo', 'Effective To')}
            type="date"
            value={recipe.effectiveTo?.substring(0, 10) ?? ''}
            onChange={(e) => onMetadataChange?.({ effectiveTo: String(e.target.value || '') })}
            disabled={!editable}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Stack>
      </Stack>

      {/* Step cards with DnD reordering, laid out as a responsive grid */}
      {editable && (
        <Stack direction="row"
          justifyContent="flex-end"
          sx={{ pt: 8, pb: 1 }}>
          <Button size="md"
            startIcon={<Add />}
            variant="primary"
            onClick={() => { resetStepDialog(); setAddDialogOpen(true); }}>
            {t('build.addStep')}
          </Button>
        </Stack>
      )}
      {steps.length > 0 ? (
        <DndContext sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)}
            strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 4,
                rowGap: 4,
                py: 1,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  md: 'repeat(3, 1fr)',
                },
                alignItems: 'stretch',
              }}
            >
              {steps.map((step, index) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={index}
                  editable={editable}
                  productionMethodName={productionMethodName(step.productionMethodId)}
                  onEdit={openEditStep}
                  onRemove={(stepId) => setStepPendingDelete(steps.find((step) => step.id === stepId) ?? null)}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Typography variant="textSM"
          color="text.secondary">{t('build.noSteps')}</Typography>
      )}

      {/* Add/Edit Step Dialog */}
      <Dialog open={addDialogOpen || editingStep !== null}
        onClose={resetStepDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{editingStep ? t('build.editStep') : t('build.addStep')}</DialogTitle>
        <DialogContent>
          <Stack spacing={4}
            sx={{ mt: 2 }}>
            {error && <Alert severity="error"
              onClose={() => setError(null)}>{error}</Alert>}
            <TextField
              fullWidth
              label={t('build.stepTitle')}
              value={stepForm.title}
              onChange={(e) => setStepForm((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('build.stepInstruction')}
              value={stepForm.instruction}
              onChange={(e) => setStepForm((p) => ({ ...p, instruction: e.target.value }))}
            />
            <TextField
              fullWidth
              select
              label={t('build.stepProductionMethod')}
              value={stepForm.productionMethodId}
              onChange={(e) => setStepForm((p) => ({ ...p, productionMethodId: e.target.value === '' ? '' : Number(e.target.value) }))}
            >
              <MenuItem value="">{t('create.none')}</MenuItem>
              {productionMethods.map((pm) => (
                <MenuItem key={pm.id}
                  value={pm.id}>{pm.name}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row"
              spacing={2}>
              <TextField
                type="number"
                label={t('build.stepDuration')}
                value={stepForm.durationMinutes}
                onChange={(e) => setStepForm((p) => ({ ...p, durationMinutes: e.target.value }))}
                sx={{ width: 200 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label={t('build.stepTemperature')}
                value={stepForm.temperature}
                onChange={(e) => setStepForm((p) => ({ ...p, temperature: e.target.value }))}
                placeholder={t('build.stepTemperaturePlaceholder')}
                sx={{ width: 200 }}
              />
            </Stack>
            <Autocomplete
              multiple
              freeSolo
              options={equipmentOptions}
              value={stepForm.equipmentNames}
              onChange={(_, value) => setStepForm((current) => ({ ...current, equipmentNames: deduplicateEquipment(value) }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key}
                    label={option}
                    size="small"
                    {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField {...params}
                  label={t('build.stepEquipment')}
                  placeholder={t('build.stepEquipmentPlaceholder')}
                  helperText="Select equipment or type a new value and press Enter."
                />
              )}
            />
            <Stack spacing={1}>
              <Typography variant="textSM"
                fontWeight={600}>Step image</Typography>
              <input
                ref={imageInputRef}
                type="file"
                aria-label="Choose step image"
                accept="image/jpeg,image/png,image/webp"
                disabled={isSubmitting}
                style={{ display: 'none' }}
                onChange={(event) => { const file = event.target.files?.[0]; if (file) selectImage(file); event.currentTarget.value = ''; }} />
              {(stepForm.imagePreviewUrl || (!stepForm.removeExistingImage && editingStep?.mediaUrl)) ? (
                <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                  <Box component="img"
                    src={stepForm.imagePreviewUrl ?? editingStep?.mediaUrl ?? ''}
                    alt="Step preview"
                    sx={{ width: '100%', height: 180, display: 'block', objectFit: 'cover', bgcolor: 'action.hover' }} />
                  <Stack direction="row"
                    spacing={1}
                    sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <IconButton size="small"
                      aria-label="Replace step image"
                      disabled={isSubmitting}
                      onClick={() => imageInputRef.current?.click()}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', '&:hover': { bgcolor: 'common.white' } }}>
                      <CloudUploadOutlined fontSize="small" />
                    </IconButton>
                    {editingStep && <IconButton size="small"
                      aria-label="Remove step image"
                      color="error"
                      disabled={isSubmitting}
                      onClick={removeSelectedImage}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', '&:hover': { bgcolor: 'common.white' } }}>
                      <Delete fontSize="small" />
                    </IconButton>}
                  </Stack>
                </Box>
              ) : (
                <Box component="button"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => imageInputRef.current?.click()}
                  sx={{ minHeight: 144, width: '100%', border: '2px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: 'text.secondary', cursor: isSubmitting ? 'default' : 'pointer', '&:hover': isSubmitting ? undefined : { borderColor: 'primary.main', bgcolor: 'action.selected' } }}>
                  <ImageOutlined sx={{ fontSize: 32, color: 'text.disabled' }} />
                  <Typography variant="textSM"
                    fontWeight={600}>Add a step image</Typography>
                  <Typography variant="textXS">JPEG, PNG, or WebP</Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetStepDialog}
            variant="secondary">{t('actions.cancel')}</Button>
          <Button
            onClick={editingStep ? handleUpdateStep : handleAddStep}
            variant="primary"
            disabled={!stepForm.title.trim() || !stepForm.instruction.trim() || isSubmitting}>
            {pendingMediaStepId ? 'Retry image action' : editingStep ? t('actions.save') : t('build.addStep')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={stepPendingDelete !== null}
        icon={<Trash01 />}
        variant="bad"
        title="Delete step?"
        message={`Are you sure you want to delete “${stepPendingDelete?.title ?? ''}”?`}
        confirmButtonText={isSubmitting ? 'Deleting...' : t('actions.delete')}
        cancelButtonText={t('actions.cancel')}
        isProcessing={isSubmitting}
        onConfirm={async () => {
          if (stepPendingDelete && await handleRemoveStep(stepPendingDelete.id)) setStepPendingDelete(null);
        }}
        onCancel={() => setStepPendingDelete(null)}
      />
      <MessagesSnackbar
        open={successMessage !== null}
        onClose={() => setSuccessMessage(null)}
        message={successMessage ?? ''}
        severity="success"
        duration={5000}
      />
    </Stack>
  );
};

export default MethodTab;